var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", {
  value,
  configurable: true
});

// src/ThemeContext.ts
import * as React from "react";
var initialContext = {
  setColorTheme: () => {}
};
var ThemeContext = React.createContext(initialContext);
if (process.env.NODE_ENV !== "production") {
  ThemeContext.displayName = "ThemeContext";
}
function useTheme() {
  return React.useContext(ThemeContext);
}
__name(useTheme, "useTheme");

// src/useThemeBehavior.ts
import * as React2 from "react";

// src/common.ts
var ColorModeValues = Object.freeze(["auto", "light-only", "dark-only"]);
var DefaultColorModeValue = ColorModeValues[0];
var StorageKey = {
  COLOR: "@seed-design/scale-color",
  PLATFORM: "@seed-design/platform"
};

// src/useThemeBehavior.ts
function useThemeBehavior(_ref) {
  let {
    mode = "auto"
  } = _ref;
  const [colorTheme, setColorTheme] = React2.useState(void 0);
  const themeContext = React2.useMemo(() => ({
    setColorTheme
  }), []);
  React2.useEffect(() => {
    if (mode !== "auto") {
      document.documentElement.dataset.seed = mode;
    }
  }, []);
  React2.useEffect(() => {
    if (!document.documentElement.dataset.seedPlatform) {
      document.documentElement.dataset.seedPlatform = "ios";
    }
  }, []);
  React2.useEffect(() => {
    if (typeof window !== "undefined") {
      const color = localStorage.getItem(StorageKey.COLOR);
      setColorTheme(color ? color : "system");
    }
  }, []);
  React2.useEffect(() => {
    if (!colorTheme) return;
    if (mode === "auto") {
      document.documentElement.dataset.seedScaleColor = colorTheme;
      if (colorTheme === "system") {
        if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
          document.documentElement.dataset.seedScaleColor = "dark";
        }
        if (window.matchMedia("(prefers-color-scheme: light)").matches) {
          document.documentElement.dataset.seedScaleColor = "light";
        }
        localStorage.removeItem(StorageKey.COLOR);
      } else {
        localStorage.setItem(StorageKey.COLOR, colorTheme);
      }
    }
    if (mode === "dark-only") {
      document.documentElement.dataset.seedScaleColor = "dark";
      localStorage.setItem(StorageKey.COLOR, "dark");
    }
    if (mode === "light-only") {
      document.documentElement.dataset.seedScaleColor = "light";
      localStorage.setItem(StorageKey.COLOR, "light");
    }
  }, [colorTheme]);
  return themeContext;
}
__name(useThemeBehavior, "useThemeBehavior");

// src/server.ts
var generateNoFlashScript = /* @__PURE__ */__name(_ref2 => {
  let {
    mode = "auto"
  } = _ref2;
  return `
		(function(window, document, mode) {
			try {
				if (mode !== 'auto') {
					document.documentElement.dataset.seed = mode;
				}
			} catch (e) {}
			
			try {
				var color = window.localStorage.getItem('${StorageKey.COLOR}');
				if (color) {
					document.documentElement.dataset.seedScaleColor = color;
				} else {
					var prefersLight = window.matchMedia('(prefers-color-scheme: light)');
  				var prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
					if (prefersLight.matches) {
						document.documentElement.dataset.seedScaleColor = 'light';
						if ('addEventListener' in prefersLight) {
							prefersLight.addEventListener('change', apply);
						} else if ('addListener' in prefersLight) {
							prefersLight.addListener(apply);
						}
					} else if (prefersDark.matches) {
						document.documentElement.dataset.seedScaleColor = 'dark';
						if ('addEventListener' in prefersDark) {
							prefersDark.addEventListener('change', apply);
						} else if ('addListener' in prefersDark) {
							prefersDark.addListener(apply);
						}
					}
				}

				function apply() {
					document.documentElement.dataset.seedScaleColor = prefersDark.matches ? 'dark' : 'light';
					document.documentElement.dataset.seedScaleLetterSpacing = 'ios';
				}
			} catch (e) {}

			try {
				var platform = window.localStorage.getItem('${StorageKey.PLATFORM}');
				if (platform) {
					document.documentElement.dataset.seedPlatform = platform;
				} else if (typeof window.AndroidFunction !== 'undefined') {
					document.documentElement.dataset.seedPlatform = 'android';
				} else if (typeof window.webkit !== 'undefined' && typeof window.webkit.messageHandlers !== 'undefined') {
					document.documentElement.dataset.seedPlatform = 'ios';
				} else {
					document.documentElement.dataset.seedPlatform = 'ios';
				}
			} catch (e) {}
		})(window, document, '${mode}');
	`;
}, "generateNoFlashScript");
export { ColorModeValues, DefaultColorModeValue, StorageKey, ThemeContext, generateNoFlashScript, useTheme, useThemeBehavior };
/*! For license information please see index.mjs.LEGAL.txt */